import React, { useEffect, useState, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import { createIntakeLink, listIntakeLinks, resendIntakeLink, retryIntakeLink, getIntakeLinkDetail, getIntakeLinkDocumentUrl, absoluteUrl } from '../lib/intake-link-api';
// Status/flag wording is shared with Zephyr, the standalone front-office
// site — a state gains its words once, in lib/intake-link-model.js.
import { STATUS_LABEL, FLAG_LABEL } from '../lib/intake-link-model';

// Badge classes are this panel's own presentation (styles/base.css, teal
// theme) and stay here; Zephyr paints the same statuses its way.
const STATUS_CLASS = {
  sent: 'il-badge-sent', opened: 'il-badge-opened', submitted: 'il-badge-opened', processing: 'il-badge-opened',
  completed: 'il-badge-done', flagged: 'il-badge-flag', expired: 'il-badge-sent', voided: 'il-badge-sent',
};

function FreshLink({ result }) {
  const url = absoluteUrl(result.path);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  useEffect(() => { QRCode.toDataURL(url, { width: 220 }).then(setQr).catch(() => {}); }, [url]);
  return (
    <div className="il-fresh">
      <code>{url}</code>
      <div className="il-fresh-actions">
        <button onClick={async () => {
          try { await navigator.clipboard.writeText(url); if (!mountedRef.current) return; setCopied(true); setTimeout(() => { if (!mountedRef.current) return; setCopied(false); }, 1500); }
          catch (_) {}
        }}>
          {copied ? '✓ Copied' : 'Copy link'}
        </button>
        <a href={`mailto:?subject=${encodeURIComponent('Your new-patient intake')}&body=${encodeURIComponent(`Please complete your intake before your visit: ${url}`)}`}>Email it</a>
      </div>
      {qr && <img className="il-qr" src={qr} alt="QR code for the intake link" />}
      <p className="il-note">One-time use · expires {new Date(result.expiresAt).toLocaleDateString()} · save it now — it won't be shown after you leave this page</p>
    </div>
  );
}

// Retained card images (recovery copies) — presigned download per image.
function CardDownloads({ linkId, documents }) {
  const [err, setErr] = useState(null);
  if (!documents || documents.length === 0) return null;
  async function open(docId) {
    setErr(null);
    try {
      const { url } = await getIntakeLinkDocumentUrl(linkId, docId);
      window.open(url, '_blank', 'noopener');
    } catch (e) { setErr(e.message || 'Download failed.'); }
  }
  return (
    <div>
      <strong>Card images:</strong>{' '}
      {documents.map((doc) => (
        <button key={doc.id} className="il-resend" onClick={() => open(doc.id)}>
          {doc.cardRank} {doc.side}
        </button>
      ))}
      {err && <span className="il-flag il-flag-detail"> {err}</span>}
    </div>
  );
}

// Full staged detail for a submission — what the patient actually entered, so
// staff can fix it in Elation by hand, verify a retry, or re-download a
// retained card image if the Elation copy is lost.
function LinkDetail({ linkId, data }) {
  if (!data) return <div className="il-note">Loading…</div>;
  const d = data.draft || {};
  const about = d.about || {};
  const contact = d.contact || {};
  const ins = d.insurance || {};
  const hasDraft = Object.keys(d).length > 0;
  const docs = data.documents || [];
  if (!hasDraft) {
    // Completed links purge the typed draft but retain card images for a
    // recovery window; expired links purge both.
    return (
      <div className="il-detail">
        <div className="il-note">Typed submission no longer staged (purged after completion/expiry).</div>
        <CardDownloads linkId={linkId} documents={docs} />
      </div>
    );
  }
  return (
    <div className="il-detail">
      <div><strong>Patient:</strong> {[about.firstName, about.lastName].filter(Boolean).join(' ') || '—'} · DOB {about.dob || '—'} · {about.sex || 'Sex unknown'}</div>
      <div><strong>Contact:</strong> {[contact.address1, contact.city, contact.state, contact.zip].filter(Boolean).join(', ') || '—'}
        {contact.phone ? ` · ${contact.phone}` : ''}{contact.email ? ` · ${contact.email}` : ''}</div>
      <div><strong>Reason for visit:</strong> {d.reason || '—'}</div>
      <div><strong>Insurance:</strong> {ins.mode === 'photos'
        ? `card photos (${docs.length} image${docs.length === 1 ? '' : 's'} staged)`
        : ins.mode === 'manual'
          ? (ins.manual || []).map((m) => `${m.rank || 'primary'}: ${m.carrierName || '?'} #${m.memberId || '?'}${m.groupId ? ` grp ${m.groupId}` : ''}`).join(' · ') || 'manual (empty)'
          : 'skipped'}</div>
      <CardDownloads linkId={linkId} documents={docs} />
      {data.error && <div><strong>Error:</strong> {data.error}</div>}
    </div>
  );
}

export function IntakeLinksPanel() {
  const [rows, setRows] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [fresh, setFresh] = useState(null); // { id, path, expiresAt }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null); // { id, data: null | detail payload }
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const reload = useCallback(async () => {
    try {
      const data = await listIntakeLinks();
      if (!mountedRef.current) return;
      setRows(data);
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  async function onCreate() {
    setBusy(true); setError(null);
    try {
      const r = await createIntakeLink(displayName.trim(), sentTo.trim());
      if (!mountedRef.current) return;
      setFresh(r); setDisplayName(''); setSentTo('');
      await reload();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
    finally { if (mountedRef.current) setBusy(false); }
  }

  async function onResend(id) {
    setBusy(true); setError(null);
    try {
      const r = await resendIntakeLink(id);
      if (!mountedRef.current) return;
      setFresh(r);
      await reload();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
    finally { if (mountedRef.current) setBusy(false); }
  }

  async function onRetry(id) {
    setBusy(true); setError(null);
    try {
      await retryIntakeLink(id);
      if (!mountedRef.current) return;
      await reload();
    } catch (e) {
      if (!mountedRef.current) return;
      setError(e.message);
    }
    finally { if (mountedRef.current) setBusy(false); }
  }

  async function onToggleDetail(id) {
    if (detail && detail.id === id) { setDetail(null); return; }
    setDetail({ id, data: null });
    try {
      const data = await getIntakeLinkDetail(id);
      if (!mountedRef.current) return;
      setDetail((cur) => (cur && cur.id === id ? { id, data } : cur));
    } catch (e) {
      if (!mountedRef.current) return;
      setDetail(null);
      setError(e.message);
    }
  }

  return (
    <div className="il-panel">
      <h3>Intake links</h3>
      {error && <div className="il-error">Error: {error}</div>}
      <div className="il-form">
        <input aria-label="Patient name for tracking" placeholder="Patient name (optional — tracking only, never sent)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        <input aria-label="Where you will send it" placeholder="Phone/email you'll send it to (optional)" value={sentTo} onChange={(e) => setSentTo(e.target.value)} />
        {/* The label is optional (owner ruling R5) — the server names an
            unlabelled link "New patient", the same word the one-tap channels
            have always used. */}
        <button disabled={busy} onClick={onCreate}>Generate link</button>
      </div>
      {fresh && <FreshLink result={fresh} />}
      {rows && rows.length === 0 && <p className="il-note">No links yet.</p>}
      {rows && rows.length > 0 && (
        <table className="il-table">
          <thead><tr><th scope="col">Patient</th><th scope="col">Created</th><th scope="col">Status</th><th scope="col">Result</th><th scope="col" aria-label="Actions" /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr>
                  <td>{r.patientName || r.displayName}</td>
                  <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                  <td><span className={`il-badge ${STATUS_CLASS[r.status] || ''}`}>{STATUS_LABEL[r.status] || r.status}</span></td>
                  <td>
                    {r.elationPatientId && <a href={`https://app.elationemr.com/patient/${encodeURIComponent(r.elationPatientId)}/`} target="_blank" rel="noreferrer">Chart #{r.elationPatientId}</a>}
                    {(r.flags || []).map((f) => <div key={f} className="il-flag">{FLAG_LABEL[f] || f}</div>)}
                    {r.error && <div className="il-flag il-flag-detail">{r.error}</div>}
                  </td>
                  <td>
                    {['sent', 'opened', 'expired'].includes(r.status) && (
                      <button className="il-resend" disabled={busy} aria-label={`Resend intake link for ${r.displayName}`} onClick={() => onResend(r.id)}>Resend</button>
                    )}
                    {r.status === 'flagged' && (
                      <>
                        <button className="il-resend" disabled={busy} aria-label={`Retry processing for ${r.displayName}`} onClick={() => onRetry(r.id)}>Retry</button>
                        <button className="il-resend" aria-expanded={detail?.id === r.id} aria-label={`Show submission details for ${r.displayName}`} onClick={() => onToggleDetail(r.id)}>
                          {detail?.id === r.id ? 'Hide details' : 'Details'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {detail?.id === r.id && (
                  <tr className="il-detail-row">
                    <td colSpan={5}><LinkDetail linkId={r.id} data={detail.data} /></td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
